/**
 * Quest Manager
 *
 * Manages quest state, progress tracking, completion, and rewards.
 * Follows the Manager singleton pattern from architecture.
 */

import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { saveManager } from '@/systems/persistence/SaveManager';
import { GameEvent, FishInstance, Quality, PlayerState, QuestDefinition, ActiveQuest, QuestRewardType, QuestObjectiveType, QuestObjectiveFilters, TrapPullResult } from '@/types';
import { evaluateQuestUnlock } from '@/utils';
import { marketManager } from '@/systems/shops/MarketManager';

export class QuestManager {
  private unsubscribeFunctions: Array<() => void> = [];
  private dismissedNotificationIds = new Set<string>();

  /**
   * Accept/start a quest
   */
  acceptQuest(questDefinition: QuestDefinition): boolean {
    const state = useGameStore.getState();

    // Check if already active
    const alreadyActive = state.playerState.quests.activeQuests.some(
      (q) => q.questId === questDefinition.id
    );
    if (alreadyActive) {
      console.warn(`Quest ${questDefinition.id} already active`);
      return false;
    }

    // Check if can be accepted
    if (!evaluateQuestUnlock(questDefinition, state.playerState)) {
      console.warn(`Quest ${questDefinition.id} unlock requirements not met`);
      return false;
    }

    // Create active quest
    const activeQuest: ActiveQuest = {
      questId: questDefinition.id,
      startedAt: Date.now(),
      expiresAt: questDefinition.expiresAt,
      objectives: questDefinition.objectives.map((obj) => ({
        objectiveId: obj.id,
        currentValue: 0,
        targetValue: obj.targetValue,
        completed: false,
      })),
      completed: false,
      claimedRewards: false,
    };

    // Add to active quests + save definition for procedural quests
    useGameStore.setState((draft) => {
      draft.playerState.quests.activeQuests.push(activeQuest);

      // Persist the quest definition so it survives page refresh
      // (procedural quests are generated with unique IDs that won't exist after reload)
      if (!draft.playerState.quests.acceptedQuestDefinitions) {
        draft.playerState.quests.acceptedQuestDefinitions = {};
      }
      draft.playerState.quests.acceptedQuestDefinitions[questDefinition.id] = questDefinition;
    });

    // Save state
    saveManager.save();

    // Emit event
    eventBus.emit(GameEvent.QUEST_ACCEPTED, {
      questId: questDefinition.id,
      quest: questDefinition,
    });

    return true;
  }

  /**
   * Update quest progress based on a game event
   * Called by event listeners when player does actions
   */
  updateQuestProgress(
    questId: string,
    objectiveId: string,
    incrementValue: number = 1
  ): void {
    const state = useGameStore.getState();

    const activeQuest = state.playerState.quests.activeQuests.find(
      (q) => q.questId === questId
    );

    if (!activeQuest) {
      return; // Quest not active
    }

    const objective = activeQuest.objectives.find(
      (obj) => obj.objectiveId === objectiveId
    );

    if (!objective) {
      return; // Objective not found
    }

    if (objective.completed) {
      return; // Already completed
    }

    // Update progress
    useGameStore.setState((draft) => {
      const quest = draft.playerState.quests.activeQuests.find(
        (q) => q.questId === questId
      );
      if (!quest) return;

      const obj = quest.objectives.find((o) => o.objectiveId === objectiveId);
      if (!obj) return;

      obj.currentValue = Math.min(
        obj.currentValue + incrementValue,
        obj.targetValue
      );

      // Check if objective completed
      if (obj.currentValue >= obj.targetValue) {
        obj.completed = true;
      }

      // Check if all objectives completed
      const allCompleted = quest.objectives.every((o) => o.completed);
      if (allCompleted && !quest.completed) {
        quest.completed = true;
        quest.completedAt = Date.now();

        // Emit completion event
        eventBus.emit(GameEvent.QUEST_COMPLETED, {
          questId,
          completedAt: Date.now(),
        });
      } else {
        // Emit progress event
        eventBus.emit(GameEvent.QUEST_PROGRESS, {
          questId,
          objectiveId,
          currentValue: obj.currentValue,
          targetValue: obj.targetValue,
        });
      }
    });

    // Save state after progress update
    saveManager.save();
  }

  /**
   * Claim quest rewards
   * Can only be called once per quest completion
   */
  claimRewards(questId: string, questDefinition: QuestDefinition): boolean {
    const state = useGameStore.getState();

    const activeQuest = state.playerState.quests.activeQuests.find(
      (q) => q.questId === questId
    );

    if (!activeQuest) {
      console.warn(`Quest ${questId} not found in active quests`);
      return false;
    }

    if (!activeQuest.completed) {
      console.warn(`Quest ${questId} not completed yet`);
      return false;
    }

    if (activeQuest.claimedRewards) {
      console.warn(`Quest ${questId} rewards already claimed`);
      return false;
    }

    // Snapshot state BEFORE applying rewards (for newly unlocked quest detection)
    const previousPlayerState = JSON.parse(
      JSON.stringify(useGameStore.getState().playerState)
    ) as PlayerState;

    // Apply rewards
    this.applyRewards(questDefinition);

    // Mark rewards as claimed
    useGameStore.setState((draft) => {
      const quest = draft.playerState.quests.activeQuests.find(
        (q) => q.questId === questId
      );
      if (quest) {
        quest.claimedRewards = true;
      }

      // Move to completed quests
      draft.playerState.quests.completedQuests.push(questId);

      // Remove from active quests
      draft.playerState.quests.activeQuests =
        draft.playerState.quests.activeQuests.filter(
          (q) => q.questId !== questId
        );

      // Clean up saved definition
      if (draft.playerState.quests.acceptedQuestDefinitions) {
        delete draft.playerState.quests.acceptedQuestDefinitions[questId];
      }
    });

    // Save state immediately after claiming rewards
    saveManager.save();

    // Emit event
    eventBus.emit(GameEvent.REWARDS_CLAIMED, {
      questId,
      timestamp: Date.now(),
    });

    // Check for chain follow-up quest
    this.checkForNextQuest(questId, questDefinition);

    // Check for newly unlocked quests (rewards may have changed unlock eligibility)
    this.checkForNewlyUnlockedQuests(previousPlayerState);

    return true;
  }

  /**
   * Apply quest rewards to player state
   */
  private applyRewards(questDefinition: QuestDefinition): void {
    const state = useGameStore.getState();

    for (const reward of questDefinition.rewards) {
      switch (reward.type) {
        case QuestRewardType.CURRENCY:
          state.addCurrency(Number(reward.value));
          break;

        case QuestRewardType.INVENTORY_EXPANSION:
          useGameStore.setState((draft) => {
            draft.playerState.inventory.capacity += Number(reward.value);
          });
          break;

        case QuestRewardType.EQUIPMENT:
          // This would need EquipmentManager integration
          // state.addOwnedEquipment(String(reward.value), { ... });
          break;

        case QuestRewardType.UNLOCK_SHOP:
          useGameStore.setState((draft) => {
            const shopId = String(reward.value);
            // Determine if it's an outfitter or market
            // This would need shop registry to check
            draft.playerState.shops.unlockedOutfitters.push(shopId);
          });
          break;

        case QuestRewardType.UNLOCK_ZONE:
          useGameStore.setState((draft) => {
            draft.playerState.unlockedZones.push(String(reward.value));
          });
          break;

        case QuestRewardType.UNLOCK_BUDDY:
          // This would need BuddyManager integration
          break;

        case QuestRewardType.EXPERIENCE:
          // Future: Add XP to player progression
          break;
      }
    }
  }

  /**
   * Check if the completed quest has a follow-up quest to auto-offer
   */
  private checkForNextQuest(completedQuestId: string, questDefinition: QuestDefinition): void {
    if (!questDefinition.nextQuestId) return;

    const nextQuestDef = marketManager.getQuestDefinition(questDefinition.nextQuestId);
    if (!nextQuestDef) return;

    const state = useGameStore.getState();

    // Don't offer if already active or completed
    const alreadyActive = state.playerState.quests.activeQuests.some(
      (q) => q.questId === nextQuestDef.id
    );
    const alreadyCompleted = state.playerState.quests.completedQuests.includes(nextQuestDef.id);
    if (alreadyActive || alreadyCompleted) return;

    // Check unlock conditions
    if (!evaluateQuestUnlock(nextQuestDef, state.playerState)) return;

    // Set pending offer in store
    useGameStore.getState().setPendingQuestOffer({
      completedQuestId,
      nextQuest: nextQuestDef,
    });

    // Emit event
    eventBus.emit(GameEvent.NEXT_QUEST_AVAILABLE, {
      completedQuestId,
      nextQuestDefinition: nextQuestDef,
    });
  }

  /**
   * Check if any new quests became unlocked after rewards were applied
   * Compares current state against pre-reward snapshot
   */
  private checkForNewlyUnlockedQuests(previousState: PlayerState): void {
    const storeState = useGameStore.getState();
    const currentState = storeState.playerState;
    const allQuests = marketManager.getAllQuestDefinitions();

    for (const quest of allQuests) {
      // Skip if already active, completed, or failed
      const isActive = currentState.quests.activeQuests.some((q) => q.questId === quest.id);
      const isCompleted = currentState.quests.completedQuests.includes(quest.id);
      const isFailed = currentState.quests.failedQuests.includes(quest.id);
      if (isActive || isCompleted || isFailed) continue;

      // Skip if this is the pending quest offer (already handled by checkForNextQuest)
      const pendingOffer = storeState.pendingQuestOffer;
      if (pendingOffer && pendingOffer.nextQuest.id === quest.id) continue;

      // Skip if already in newly unlocked list (prevents duplicate events)
      if (storeState.newlyUnlockedQuests.some((n) => n.questId === quest.id)) continue;

      // Skip if previously dismissed this session
      if (this.dismissedNotificationIds.has(quest.id)) continue;

      // Check if NOW unlockable but was NOT before
      const nowUnlocked = evaluateQuestUnlock(quest, currentState);
      const wasUnlocked = evaluateQuestUnlock(quest, previousState);

      if (nowUnlocked && !wasUnlocked) {
        // Newly unlocked quest
        useGameStore.getState().addNewlyUnlockedQuest(quest.id, quest.name);

        eventBus.emit(GameEvent.NEW_QUEST_UNLOCKED, {
          questId: quest.id,
          questName: quest.name,
        });
      }
    }
  }

  /**
   * Scan all quest definitions for newly available quests.
   * Unlike checkForNewlyUnlockedQuests, this doesn't need a before/after snapshot.
   * Called after key state changes (sales, purchases, etc.) and on startup.
   */
  scanForAvailableQuests(): void {
    const storeState = useGameStore.getState();
    const currentState = storeState.playerState;
    const allQuests = marketManager.getAllQuestDefinitions();
    const pendingOffer = storeState.pendingQuestOffer;

    for (const quest of allQuests) {
      // Skip if already active
      if (currentState.quests.activeQuests.some((q) => q.questId === quest.id)) continue;
      // Skip if already completed
      if (currentState.quests.completedQuests.includes(quest.id)) continue;
      // Skip if already failed
      if (currentState.quests.failedQuests.includes(quest.id)) continue;
      // Skip if pending offer (already handled by checkForNextQuest)
      if (pendingOffer && pendingOffer.nextQuest.id === quest.id) continue;
      // Skip if already in newly unlocked list
      if (storeState.newlyUnlockedQuests.some((n) => n.questId === quest.id)) continue;
      // Skip if previously dismissed this session
      if (this.dismissedNotificationIds.has(quest.id)) continue;

      // Check if unlockable
      if (evaluateQuestUnlock(quest, currentState)) {
        storeState.addNewlyUnlockedQuest(quest.id, quest.name);
        eventBus.emit(GameEvent.NEW_QUEST_UNLOCKED, {
          questId: quest.id,
          questName: quest.name,
        });
      }
    }
  }

  /**
   * Dismiss a quest notification and prevent re-surfacing this session.
   * Called when the player clicks "Dismiss" on a NewQuestNotification.
   */
  dismissQuestNotification(questId: string): void {
    this.dismissedNotificationIds.add(questId);
    useGameStore.getState().dismissNewlyUnlockedQuest(questId);
  }

  /**
   * Accept the pending quest offer (chain follow-up)
   */
  acceptPendingQuestOffer(): boolean {
    const store = useGameStore.getState();
    const offer = store.pendingQuestOffer;
    if (!offer) return false;

    // If quest was already accepted (e.g., via NewQuestNotification), just clear the offer
    const alreadyActive = store.playerState.quests.activeQuests.some(
      (q) => q.questId === offer.nextQuest.id
    );
    if (alreadyActive) {
      store.clearPendingQuestOffer();
      return false;
    }

    const accepted = this.acceptQuest(offer.nextQuest);
    if (accepted) {
      store.clearPendingQuestOffer();
    }
    return accepted;
  }

  /**
   * Abandon a quest
   */
  abandonQuest(questId: string): boolean {
    useGameStore.setState((draft) => {
      draft.playerState.quests.activeQuests =
        draft.playerState.quests.activeQuests.filter(
          (q) => q.questId !== questId
        );

      // Clean up saved definition
      if (draft.playerState.quests.acceptedQuestDefinitions) {
        delete draft.playerState.quests.acceptedQuestDefinitions[questId];
      }
    });

    saveManager.save();

    eventBus.emit(GameEvent.QUEST_ABANDONED, { questId });

    return true;
  }

  /**
   * Fail a quest (due to expiration or failure conditions)
   */
  failQuest(questId: string): void {
    useGameStore.setState((draft) => {
      // Move to failed quests
      draft.playerState.quests.failedQuests.push(questId);

      // Remove from active quests
      draft.playerState.quests.activeQuests =
        draft.playerState.quests.activeQuests.filter(
          (q) => q.questId !== questId
        );

      // Clean up saved definition
      if (draft.playerState.quests.acceptedQuestDefinitions) {
        delete draft.playerState.quests.acceptedQuestDefinitions[questId];
      }
    });

    saveManager.save();

    eventBus.emit(GameEvent.QUEST_FAILED, { questId });
  }

  /**
   * Check for expired quests and fail them
   */
  checkExpiredQuests(): void {
    const state = useGameStore.getState();
    const now = Date.now();

    for (const quest of state.playerState.quests.activeQuests) {
      if (quest.expiresAt && now > quest.expiresAt) {
        this.failQuest(quest.questId);

        eventBus.emit(GameEvent.QUEST_EXPIRED, {
          questId: quest.questId,
        });
      }
    }
  }

  /**
   * Check if a quest objective filter matches a fish
   */
  fishMatchesObjectiveFilters(
    fish: FishInstance,
    filters?: QuestObjectiveFilters
  ): boolean {
    if (!filters) {
      return true; // No filters = matches all
    }

    // Check species
    if (filters.speciesId) {
      const species = Array.isArray(filters.speciesId)
        ? filters.speciesId
        : [filters.speciesId];
      if (!species.includes(fish.definitionId)) {
        return false;
      }
    }

    // Check quality
    if (filters.quality) {
      const qualities = Array.isArray(filters.quality)
        ? filters.quality
        : [filters.quality];
      if (!qualities.includes(fish.quality)) {
        return false;
      }
    }

    // Check weight
    if (filters.minWeight !== undefined && fish.weight) {
      if (fish.weight < filters.minWeight) {
        return false;
      }
    }

    // Check length (would need length on fish)
    // if (filters.minLength !== undefined && fish.length) { ... }

    return true;
  }

  /**
   * Update all active quests based on a fish caught event
   */
  onFishCaught(fish: FishInstance): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      // Get quest definition to check objective types
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      // Check each objective
      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;

        // Only process CATCH_FISH objectives
        if (objectiveDef.type !== QuestObjectiveType.CATCH_FISH) continue;

        // Check if fish matches filters
        if (this.fishMatchesObjectiveFilters(fish, objectiveDef.filters)) {
          this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
        }
      }
    }
  }

  /**
   * Update all active quests based on a fish sold event
   */
  onFishSold(speciesId: string, quality: Quality, count: number, _value: number): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      // Get quest definition to check objective types
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      // Check each objective
      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;

        // Only process SELL_FISH objectives
        if (objectiveDef.type !== QuestObjectiveType.SELL_FISH) continue;

        // Check if sold fish matches filters
        const matchesSpecies = !objectiveDef.filters?.speciesId ||
          (Array.isArray(objectiveDef.filters.speciesId)
            ? objectiveDef.filters.speciesId.includes(speciesId)
            : objectiveDef.filters.speciesId === speciesId);

        const matchesQuality = !objectiveDef.filters?.quality ||
          (Array.isArray(objectiveDef.filters.quality)
            ? objectiveDef.filters.quality.includes(quality)
            : objectiveDef.filters.quality === quality);

        if (matchesSpecies && matchesQuality) {
          this.updateQuestProgress(activeQuest.questId, objectiveDef.id, count);
        }
      }
    }
  }

  /**
   * Update all active quests based on a buddy hired event
   */
  onBuddyHired(_slotNumber: number): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      // Get quest definition to check objective types
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      // Check each objective
      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;

        // Only process HIRE_BUDDY objectives
        if (objectiveDef.type !== QuestObjectiveType.HIRE_BUDDY) continue;

        // Increment progress (each buddy hire counts as +1)
        this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
      }
    }
  }

  /**
   * Update all active quests based on an equipment purchase event
   */
  onEquipmentPurchased(equipmentId: string): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;

        if (
          objectiveDef.type !== QuestObjectiveType.BUY_EQUIPMENT &&
          objectiveDef.type !== QuestObjectiveType.UNLOCK_ITEM
        ) continue;

        // Check if purchased equipment matches filters
        const matchesEquipment = !objectiveDef.filters?.equipmentId ||
          (Array.isArray(objectiveDef.filters.equipmentId)
            ? objectiveDef.filters.equipmentId.includes(equipmentId)
            : objectiveDef.filters.equipmentId === equipmentId);

        if (matchesEquipment) {
          this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
        }
      }
    }
  }

  /**
   * Update all active quests based on a sweet spot timing hit
   * Only counts Perfect and Good hits (not misses)
   */
  onSweetSpotHit(tier: 'perfect' | 'good' | 'miss'): void {
    if (tier === 'miss') return;

    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;
        if (objectiveDef.type !== QuestObjectiveType.TIMING_HIT) continue;

        this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
      }
    }
  }

  /**
   * Update all active quests based on a buddy catching a fish
   * Only updates BUDDY_CATCH_FISH objectives (not regular CATCH_FISH)
   */
  onBuddyCaughtFish(fish: FishInstance): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;
        if (objectiveDef.type !== QuestObjectiveType.BUDDY_CATCH_FISH) continue;

        if (this.fishMatchesObjectiveFilters(fish, objectiveDef.filters)) {
          this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
        }
      }
    }
  }

  /**
   * Update all active quests based on a hauler selling fish
   * Updates HAULER_SELL_FISH (by count) and HAULER_EARN_GOLD (by value) objectives
   */
  onHaulerSoldFish(fishCount: number, value: number): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;

        if (objectiveDef.type === QuestObjectiveType.HAULER_SELL_FISH) {
          this.updateQuestProgress(activeQuest.questId, objectiveDef.id, fishCount);
        } else if (objectiveDef.type === QuestObjectiveType.HAULER_EARN_GOLD) {
          this.updateQuestProgress(activeQuest.questId, objectiveDef.id, Math.floor(value));
        }
      }
    }
  }

  /**
   * Update all active quests when a trap is set
   * Increments SET_TRAP objectives
   */
  onTrapSet(): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;
        if (objectiveDef.type !== QuestObjectiveType.SET_TRAP) continue;

        this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
      }
    }
  }

  /**
   * Update all active quests when a trap is pulled
   * Increments PULL_TRAP objectives and CATCH_CRUSTACEAN objectives for each creature
   */
  onTrapPulled(result: TrapPullResult): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;

        if (objectiveDef.type === QuestObjectiveType.PULL_TRAP) {
          this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
        } else if (objectiveDef.type === QuestObjectiveType.CATCH_CRUSTACEAN) {
          // Count each creature that matches filters
          for (const { fish, definition } of result.creatures) {
            if (definition.category === 'crustacean' || definition.category === 'shellfish') {
              if (this.fishMatchesObjectiveFilters(fish, objectiveDef.filters)) {
                this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
              }
            }
          }
        }
      }
    }
  }

  /**
   * Update all active quests when a worm dig minigame is completed
   * Increments DIG_WORMS objectives
   */
  onWormDigCompleted(): void {
    const state = useGameStore.getState();

    for (const activeQuest of state.playerState.quests.activeQuests) {
      const questDef = marketManager.getQuestDefinition(activeQuest.questId);
      if (!questDef) continue;

      for (let i = 0; i < questDef.objectives.length; i++) {
        const objectiveDef = questDef.objectives[i];
        const objectiveProgress = activeQuest.objectives[i];

        if (objectiveProgress?.completed) continue;
        if (objectiveDef.type !== QuestObjectiveType.DIG_WORMS) continue;

        this.updateQuestProgress(activeQuest.questId, objectiveDef.id, 1);
      }
    }
  }

  /**
   * Initialize quest event listeners
   * Called on app startup
   */
  initialize(): void {
    // Listen to fish caught events
    const unsubFishCaught = eventBus.on(GameEvent.FISH_CAUGHT, (payload) => {
      this.onFishCaught(payload.fish);
    });
    this.unsubscribeFunctions.push(unsubFishCaught);

    // Listen to fish sold events
    const unsubFishSold = eventBus.on(GameEvent.FISH_SOLD, (payload) => {
      // payload is a SellTransaction - extract fish data from it
      for (const stack of payload.fish) {
        this.onFishSold(
          stack.definitionId,
          stack.quality as Quality,
          stack.count,
          payload.finalValue
        );
      }
      // Scan for quests unlocked by currency change
      this.scanForAvailableQuests();
    });
    this.unsubscribeFunctions.push(unsubFishSold);

    // Listen to currency gained
    const unsubCurrencyGained = eventBus.on(GameEvent.CURRENCY_GAINED, (_payload) => {
      // Handle "earn gold" quest progress
      // TODO: Implement EARN_CURRENCY quest objectives
    });
    this.unsubscribeFunctions.push(unsubCurrencyGained);

    // Listen to equipment purchased
    const unsubEquipmentPurchased = eventBus.on(GameEvent.EQUIPMENT_PURCHASED, (payload) => {
      this.onEquipmentPurchased(payload.equipmentId);
      // Scan for quests unlocked by equipment purchase
      this.scanForAvailableQuests();
    });
    this.unsubscribeFunctions.push(unsubEquipmentPurchased);

    // Listen to buddy hired events
    const unsubBuddyHired = eventBus.on(GameEvent.BUDDY_HIRED, (payload) => {
      this.onBuddyHired(payload.slotNumber);
      // Scan for quests unlocked by buddy hire
      this.scanForAvailableQuests();
    });
    this.unsubscribeFunctions.push(unsubBuddyHired);

    // Listen to sweet spot hit events (for timing quest objectives)
    const unsubSweetSpotHit = eventBus.on(GameEvent.SWEET_SPOT_HIT, (payload) => {
      this.onSweetSpotHit(payload.tier);
    });
    this.unsubscribeFunctions.push(unsubSweetSpotHit);

    // Listen to buddy caught fish events (for buddy-only quest objectives)
    const unsubBuddyCaughtFish = eventBus.on(GameEvent.BUDDY_CAUGHT_FISH, (payload) => {
      this.onBuddyCaughtFish(payload.fish);
    });
    this.unsubscribeFunctions.push(unsubBuddyCaughtFish);

    // Listen to hauler sold fish events (for hauler-only quest objectives)
    const unsubHaulerSoldFish = eventBus.on(GameEvent.HAULER_SOLD_FISH, (payload) => {
      this.onHaulerSoldFish(payload.fishCount, payload.value);
    });
    this.unsubscribeFunctions.push(unsubHaulerSoldFish);

    // Listen to trap set events (for SET_TRAP quest objectives)
    const unsubTrapSet = eventBus.on(GameEvent.TRAP_SET, () => {
      this.onTrapSet();
    });
    this.unsubscribeFunctions.push(unsubTrapSet);

    // Listen to trap pulled events (for PULL_TRAP and CATCH_CRUSTACEAN quest objectives)
    const unsubTrapPulled = eventBus.on(GameEvent.TRAP_PULLED, (result) => {
      this.onTrapPulled(result);
    });
    this.unsubscribeFunctions.push(unsubTrapPulled);

    // Listen to worm dig completed events (for DIG_WORMS quest objectives)
    const unsubWormDigCompleted = eventBus.on(GameEvent.WORM_DIG_COMPLETED, () => {
      this.onWormDigCompleted();
    });
    this.unsubscribeFunctions.push(unsubWormDigCompleted);

    // Note: No REWARDS_CLAIMED listener for scanForAvailableQuests() here.
    // claimRewards() already calls checkForNextQuest() + checkForNewlyUnlockedQuests(),
    // which fully handles quest discovery after rewards. A listener here would race
    // against checkForNextQuest() (pendingQuestOffer not yet set), causing duplicates.

    // Start checking for expired quests
    const intervalId = setInterval(() => {
      this.checkExpiredQuests();
    }, 60000); // Check every minute

    // Store cleanup for interval
    this.unsubscribeFunctions.push(() => clearInterval(intervalId));
  }

  /**
   * Cleanup event listeners
   */
  cleanup(): void {
    // Unsubscribe from all events
    this.unsubscribeFunctions.forEach((unsub) => unsub());
    this.unsubscribeFunctions = [];
    this.dismissedNotificationIds.clear();
  }
}

// Singleton instance
export const questManager = new QuestManager();
